/**
 * AdBuilder.jsx
 * Building a real advert, rather than boosting a post.
 *
 * The difference that matters is the objective: a boosted post optimises for
 * engagement, which buys cheap reactions from people who will never convert.
 * An ad here can optimise for clicks, installs or sign-ups — which is why the
 * objective is the first thing on the screen and everything else follows from
 * it.
 *
 * Laid out as: what it should do → what it looks like → who sees it → what it
 * costs. The preview on the right is the ad as Facebook will render it, so the
 * headline being too long is visible rather than a validation message.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import MediaPicker from '../components/MediaPicker.jsx';
import AdPreview from '../components/ads/AdPreview.jsx';
import LivePostPicker from '../components/ads/LivePostPicker.jsx';
import Placements from '../components/ads/Placements.jsx';
import { Check, Warning } from '../components/Icons.jsx';
import { defaultScheduleValue, formatLocalInput, formatLondon } from '../utils/time.js';

const money = (pence) => `£${((pence ?? 0) / 100).toFixed(2)}`;

export default function AdBuilder() {
  const navigate = useNavigate();

  // The shared list and the shared choice: an ad built for the wrong brand is
  // the one mistake here that costs money.
  const { brands } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [options, setOptions] = useState(null);

  const [objective, setObjective] = useState('traffic');
  const [name, setName] = useState('');

  // Where the picture and words come from: written here, or borrowed from
  // posts that are already live — at most one per platform, because two posts
  // with different captions are two different Meta objects.
  const [source, setSource] = useState('new');
  const [posts, setPosts] = useState({ facebook: null, instagram: null });
  const [placements, setPlacements] = useState(['facebook', 'instagram']);

  // The posts actually chosen, in a stable order.
  const chosenPosts = ['facebook', 'instagram'].map((k) => posts[k]).filter(Boolean);

  const [media, setMedia] = useState([]);
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [cta, setCta] = useState('LEARN_MORE');
  const [event, setEvent] = useState('COMPLETE_REGISTRATION');

  const [countries, setCountries] = useState(['GB']);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [genders, setGenders] = useState([]);
  const [interests, setInterests] = useState([]);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);

  const [pounds, setPounds] = useState('5.00');
  const [days, setDays] = useState(5);

  // When it begins: on approval, or at a time chosen here. Meta's own ad set
  // start time does the waiting, so the app need not be running that minute.
  const [startMode, setStartMode] = useState('approve');
  const [startLocal, setStartLocal] = useState(defaultScheduleValue);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const loadOptions = useCallback(async () => {
    if (!brandId) return;
    try {
      const o = await api.ads.options(brandId);
      setOptions(o);
      setLink((l) => l || brands.find((b) => b.id === brandId)?.target_url || '');
    } catch (e) {
      setError(e);
    }
  }, [brandId, brands]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // Media and live posts both belong to one brand, so switching brand clears
  // whichever was chosen. Advertising another brand's post is the expensive
  // mistake this prevents.
  useEffect(() => {
    setMedia([]);
    setPosts({ facebook: null, instagram: null });
  }, [brandId]);

  // A post can only be advertised where it was posted, so the placements are
  // derived from what was chosen rather than left separately selectable. One
  // post means one platform; one of each means both.
  useEffect(() => {
    if (source !== 'post') return;
    setPlacements(['facebook', 'instagram'].filter((k) => posts[k]));
  }, [source, posts]);

  // Coming back to a written advert restores the choice, limited to what this
  // brand has actually connected.
  useEffect(() => {
    if (source === 'new' && options?.connected) setPlacements(options.connected);
  }, [source, options?.connected]);

  async function searchInterests() {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const { interests: hits } = await api.ads.interests(query.trim());
      setFound(hits);
    } catch (e) {
      setError(e);
    } finally {
      setSearching(false);
    }
  }

  const spec = options?.objectives?.find((o) => o.key === objective);
  const missingAsset =
    spec?.needs === 'app'
      ? !options?.assets?.app
      : spec?.needs === 'pixel'
        ? !options?.assets?.pixel
        : false;

  const pence = Math.round(Number(String(pounds).replace(/[^0-9.]/g, '')) * 100);
  const total = Number.isFinite(pence) ? pence * days : 0;
  const limits = options?.limits;
  const max = options?.creative;

  // A written advert needs its own image and words. One borrowed from a live
  // post needs neither — the post already carries both — so only the link is
  // checked, and only if a button was asked for.
  const creativeReady =
    source === 'post'
      ? chosenPosts.length > 0 && (!link.trim() || /^https?:\/\/\S+$/i.test(link.trim()))
      : media.length === 1 &&
        headline.trim() &&
        headline.length <= (max?.maxHeadline ?? 40) &&
        body.trim() &&
        body.length <= (max?.maxBody ?? 125) &&
        /^https?:\/\/\S+$/i.test(link.trim());

  const valid =
    brandId &&
    creativeReady &&
    (startMode !== 'at' || Boolean(startLocal)) &&
    placements.length > 0 &&
    countries.length > 0 &&
    !missingAsset &&
    limits &&
    pence >= limits.minDailyBudgetPence &&
    pence <= limits.maxDailyBudgetPence &&
    days >= limits.minDurationDays &&
    days <= limits.maxDurationDays;

  // Why the button is off. It sits at the foot of a long screen now, so
  // "nothing happens when I click" needs an answer without scrolling back up
  // to hunt for the empty field.
  const missingReason = (() => {
    if (!brandId) return 'Choose a brand first.';
    if (missingAsset) {
      return spec?.needs === 'app'
        ? 'This objective needs an app attached to the brand, on the Ads screen.'
        : 'This objective needs a pixel attached to the brand, on the Ads screen.';
    }
    if (source === 'post') {
      if (chosenPosts.length === 0) return 'Choose a post for the advert to use.';
      if (link.trim() && !/^https?:\/\/\S+$/i.test(link.trim())) {
        return 'The button link must start with https://';
      }
    } else {
      if (media.length !== 1) return 'Choose an image for the advert.';
      if (!headline.trim()) return 'The advert needs a headline.';
      if (headline.length > (max?.maxHeadline ?? 40)) return 'The headline is too long.';
      if (!body.trim()) return 'The advert needs body text.';
      if (body.length > (max?.maxBody ?? 125)) return 'The body text is too long.';
      if (!/^https?:\/\/\S+$/i.test(link.trim())) return 'The advert needs a link starting with https://';
    }
    if (placements.length === 0) return 'Choose where it runs — Facebook, Instagram, or both.';
    if (countries.length === 0) return 'Choose at least one country.';
    if (startMode === 'at' && !startLocal) return 'Choose the date and time it should start.';
    if (limits && (pence < limits.minDailyBudgetPence || pence > limits.maxDailyBudgetPence)) {
      return `The daily budget must be between ${money(limits.minDailyBudgetPence)} and ${money(limits.maxDailyBudgetPence)}.`;
    }
    if (limits && (days < limits.minDurationDays || days > limits.maxDurationDays)) {
      return `It has to run for between ${limits.minDurationDays} and ${limits.maxDurationDays} days.`;
    }
    return null;
  })();

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.ads.createAd({
        brand_id: brandId,
        name: name.trim(),
        objective,
        creative_source: source,
        posts:
          source === 'post'
            ? chosenPosts.map((p) => ({
                platform: p.platform,
                remote_id: p.id,
                caption: p.caption,
                permalink: p.permalink,
                image_url: p.imageUrl,
              }))
            : [],
        media_id: source === 'post' ? null : media[0].id,
        headline: headline.trim(),
        body: body.trim(),
        description: description.trim(),
        link: link.trim(),
        call_to_action: cta,
        conversion_event: event,
        placements,
        // London wall-clock; the API converts. Empty means "when I start it".
        start_at: startMode === 'at' ? startLocal : null,
        countries,
        age_min: Number(ageMin),
        age_max: Number(ageMax),
        genders,
        interests: interests.map((i) => ({ id: i.id, name: i.name })),
        daily_budget_pence: pence,
        duration_days: Number(days),
      });
      setDone(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!options) return <p className="mono">Loading…</p>;

  if (done) {
    return (
      <>
        <div className="page-head">
          <div>
            <div className="page-head__eyebrow">Ads</div>
            <h1>Advert created, paused</h1>
            <p>
              {startMode === 'at'
                ? 'It is not spending, and it will not start on its own until you approve it once on the Ads screen.'
                : 'It is not spending. Start it on the Ads screen when you are ready.'}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="notice notice--info">
            <Check />
            <span>
              Up to <strong>{money(done.totalPence)}</strong> — {money(done.dailyBudgetPence)} a day
              for {done.durationDays} days
              {done.adIds?.length > 1
                ? ', across both posts. They share this one budget, and one Pause stops both.'
                : '.'}
            </span>
          </div>

          {startMode === 'at' && (
            <div className="notice notice--info">
              <Check />
              <span>
                Set to begin <strong>{formatLondon(done.startsAt)}</strong>. Approve it on the Ads
                screen and Meta starts it at that moment — nothing needs to be open or running.
              </span>
            </div>
          )}
          <div className="btn-row" style={{ marginTop: 14 }}>
            <button className="btn btn--primary" onClick={() => navigate('/ads')}>
              Go to Ads
            </button>
            <button className="btn" onClick={() => window.location.reload()}>
              Build another
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">
            <button className="linkish" onClick={() => navigate('/ads')}>
              Ads
            </button>{' '}
            / New advert
          </div>
          <h1>Build an advert</h1>
          <p>
            Your own image and words, or a post that is already live — optimised for what you
            actually want and running on Facebook, Instagram or both. Created paused; nothing
            spends until you start it.
          </p>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}

      <div className="grid">
        {/* ---------- Left: the ad ---------- */}
        <section className="card">
          <div className="card__head">
            <div className="card__head-text">
              <h2>What it should do</h2>
              <p>This is the setting that decides whether an ad works.</p>
            </div>
          </div>

          <div className="field">
            <label htmlFor="a-brand">Brand</label>
            <select id="a-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Objective</label>
            <div className="btn-row">
              {options.objectives.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={`btn btn--sm ${objective === o.key ? 'btn--primary' : ''}`}
                  onClick={() => setObjective(o.key)}
                >
                  {objective === o.key ? <Check size={15} /> : null}
                  {o.label}
                </button>
              ))}
            </div>
            <p className="hint">{spec?.hint}</p>
            {missingAsset && (
              <div className="notice notice--error">
                <Warning />
                <span>
                  {spec.needs === 'app'
                    ? 'No app attached to this brand, so Meta would have nothing to optimise towards. Attach one on the Ads screen.'
                    : 'No pixel attached to this brand, so Meta could not tell who converted. Attach one on the Ads screen.'}
                </span>
              </div>
            )}
            {spec?.needs === 'pixel' && !missingAsset && (
              <div className="field" style={{ marginTop: 10 }}>
                <label htmlFor="a-event">Count as success</label>
                <select id="a-event" value={event} onChange={(e) => setEvent(e.target.value)}>
                  {options.conversionEvents.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="card__head" style={{ marginTop: 8 }}>
            <div className="card__head-text">
              <h2>What it looks like</h2>
            </div>
          </div>

          <div className="field">
            <label>Where the picture and words come from</label>
            <div className="btn-row">
              {(options.creativeSources ?? []).map((s) => (
                <button
                  key={s.value}
                  type="button"
                  className={`btn btn--sm ${source === s.value ? 'btn--primary' : ''}`}
                  onClick={() => setSource(s.value)}
                >
                  {source === s.value ? <Check size={15} /> : null}
                  {s.label}
                </button>
              ))}
            </div>
            <p className="hint">
              {source === 'post'
                ? 'Borrows live posts as the advert, and keeps the likes and comments they have already earned. Pick one from each platform and both run on a single budget. Unlike a boost, the objective, audience and budget below are still the advert’s own.'
                : 'Your own image and words, and it never appears on your Page or grid. Runs on Facebook and Instagram at once, and needs no post to exist first.'}
            </p>
          </div>

          {source === 'post' ? (
            <LivePostPicker brandId={brandId} selected={posts} onChange={setPosts} />
          ) : (
            <>
              <MediaPicker
                brandId={brandId}
                selected={media}
                max={1}
                kind="image"
                label="Image"
                onChange={setMedia}
              />

              <div className="field">
                <label htmlFor="a-headline">
                  Headline
                  <span className={`counter${headline.length > max.maxHeadline ? ' over' : ''}`}>
                    {headline.length} / {max.maxHeadline}
                  </span>
                </label>
                <input
                  id="a-headline"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Stop rewriting the list"
                />
              </div>

              <div className="field">
                <label htmlFor="a-body">
                  Body text
                  <span className={`counter${body.length > max.maxBody ? ' over' : ''}`}>
                    {body.length} / {max.maxBody}
                  </span>
                </label>
                <textarea
                  id="a-body"
                  rows={3}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Archevot turns an overwhelming week into steps you can actually start."
                />
              </div>
            </>
          )}

          <div className="field">
            <label htmlFor="a-link">
              Link
              {source === 'post' && <span className="counter">optional</span>}
            </label>
            <input
              id="a-link"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://archevot.com"
            />
            {source === 'post' && (
              <p className="hint">
                Adds a button to the post when it runs as an advert. Leave it empty and the post
                runs exactly as published.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="a-cta">Button</label>
            <select id="a-cta" value={cta} onChange={(e) => setCta(e.target.value)}>
              {options.callToActions.map((x) => (
                <option key={x.value} value={x.value}>
                  {x.label}
                </option>
              ))}
            </select>
          </div>

          <Placements
            options={options.placements ?? []}
            value={placements}
            connected={options.connected ?? ['facebook']}
            // An existing-post advert does not choose where it runs — the
            // posts decide, so the control reports rather than asks.
            derived={source === 'post'}
            onChange={setPlacements}
          />

          <div className="field">
            <label htmlFor="a-name">Name it (optional)</label>
            <input
              id="a-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="For your reference in Ads Manager"
            />
          </div>
        </section>

        {/* ---------- Right: preview, audience, cost ---------- */}
        <div>
          <section className="card">
            <div className="card__head">
              <div className="card__head-text">
                <h2>Preview</h2>
                <p>
                  {source === 'post'
                    ? 'The post as it already exists, shown as an advert.'
                    : 'Roughly how it appears in the feed.'}
                </p>
              </div>
            </div>

            <AdPreview
              brandName={brands.find((b) => b.id === brandId)?.name}
              source={source}
              posts={chosenPosts}
              media={media}
              headline={headline}
              body={body}
              description={description}
              link={link}
              ctaLabel={options.callToActions.find((x) => x.value === cta)?.label}
              placements={placements}
            />
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__head-text">
                <h2>Who sees it</h2>
              </div>
            </div>

            <div className="field">
              <label>Countries</label>
              <div className="chips">
                {options.countries.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    className={`chip-btn${countries.includes(c.code) ? ' is-on' : ''}`}
                    onClick={() =>
                      setCountries((cs) =>
                        cs.includes(c.code) ? cs.filter((x) => x !== c.code) : [...cs, c.code],
                      )
                    }
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="field field--row">
              <div>
                <label htmlFor="a-agemin">Age from</label>
                <input
                  id="a-agemin"
                  type="number"
                  min={options.targeting.ageFloor}
                  max={options.targeting.ageCeiling}
                  value={ageMin}
                  onChange={(e) => setAgeMin(Number(e.target.value))}
                />
              </div>
              <div>
                <label htmlFor="a-agemax">to</label>
                <input
                  id="a-agemax"
                  type="number"
                  min={options.targeting.ageFloor}
                  max={options.targeting.ageCeiling}
                  value={ageMax}
                  onChange={(e) => setAgeMax(Number(e.target.value))}
                />
              </div>
              <div>
                <label>Gender</label>
                <div className="btn-row">
                  {[
                    { v: [], label: 'All' },
                    { v: [1], label: 'Men' },
                    { v: [2], label: 'Women' },
                  ].map((g) => (
                    <button
                      key={g.label}
                      type="button"
                      className={`btn btn--sm ${
                        JSON.stringify(genders) === JSON.stringify(g.v) ? 'btn--primary' : ''
                      }`}
                      onClick={() => setGenders(g.v)}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="field">
              <label htmlFor="a-interest">
                Interests
                <span className="counter">
                  {interests.length} / {options.targeting.maxInterests}
                </span>
              </label>

              {interests.length > 0 && (
                <div className="chips" style={{ marginBottom: 8 }}>
                  {interests.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      className="chip-btn is-on"
                      onClick={() => setInterests((xs) => xs.filter((x) => x.id !== i.id))}
                      title="Remove"
                    >
                      {i.name} ×
                    </button>
                  ))}
                </div>
              )}

              <div className="btn-row">
                <input
                  id="a-interest"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchInterests()}
                  placeholder="productivity, ADHD, note taking…"
                />
                <button className="btn btn--sm" onClick={searchInterests} disabled={searching}>
                  {searching ? 'Searching…' : 'Search'}
                </button>
              </div>

              {found.length > 0 && (
                <div className="library" style={{ marginTop: 10 }}>
                  {found.map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      className="lib-item"
                      disabled={
                        interests.length >= options.targeting.maxInterests ||
                        interests.some((x) => x.id === i.id)
                      }
                      onClick={() => {
                        setInterests((xs) => [...xs, i]);
                        setFound((f) => f.filter((x) => x.id !== i.id));
                      }}
                    >
                      <div className="lib-item__meta" style={{ position: 'static' }}>
                        <span>{i.name}</span>
                        <span>
                          {i.audienceLower
                            ? `${(i.audienceLower / 1_000_000).toFixed(1)}m+`
                            : 'add'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <p className="hint">
                Leave empty to let Meta find the audience itself, which is often better than a
                guess on a small budget.
              </p>
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div className="card__head-text">
                <h2>What it costs</h2>
              </div>
            </div>

            <div className="field field--row">
              <div>
                <label htmlFor="a-budget">Per day</label>
                <input id="a-budget" value={pounds} onChange={(e) => setPounds(e.target.value)} />
              </div>
              <div>
                <label htmlFor="a-days">For how many days</label>
                <input
                  id="a-days"
                  type="number"
                  min={limits.minDurationDays}
                  max={limits.maxDurationDays}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="field">
              <label>When it starts</label>
              <div className="btn-row">
                {[
                  { key: 'approve', label: 'When I start it' },
                  { key: 'at', label: 'At a set time' },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`btn btn--sm ${startMode === m.key ? 'btn--primary' : ''}`}
                    onClick={() => setStartMode(m.key)}
                  >
                    {startMode === m.key ? <Check size={15} /> : null}
                    {m.label}
                  </button>
                ))}
              </div>

              {startMode === 'at' ? (
                <>
                  <input
                    id="a-start"
                    type="datetime-local"
                    value={startLocal}
                    onChange={(e) => setStartLocal(e.target.value)}
                    style={{ marginTop: 8 }}
                  />
                  <p className="hint">
                    {startLocal ? `Begins ${formatLocalInput(startLocal)}, London time. ` : ''}
                    It still arrives paused — you approve it once, and Meta starts it at that
                    moment without anything else from you.
                  </p>
                </>
              ) : (
                <p className="hint">
                  Nothing happens until you press Start on the Ads screen, and it begins
                  immediately when you do.
                </p>
              )}
            </div>

            <div className="boost-total">
              <div className="boost-total__value">{money(total)}</div>
              <div className="boost-total__label">
                total, at most — {money(pence)} a day for {days} {days === 1 ? 'day' : 'days'}
              </div>
            </div>

            <p className="hint">
              Between {money(limits.minDailyBudgetPence)} and {money(limits.maxDailyBudgetPence)} a
              day. {money(options.settings.capPence)} monthly cap, set in Settings.
              {startMode === 'at'
                ? ' A scheduled advert counts against the cap from the moment you approve it.'
                : ''}
            </p>

            {/* The last thing on the screen, directly under the figure it
                commits you to — so the number and the button that acts on it
                are read together rather than a page apart. */}
            <div className="btn-row" style={{ marginTop: 16 }}>
              <button
                className="btn btn--primary btn--lg"
                onClick={create}
                disabled={!valid || busy}
              >
                {busy ? 'Creating…' : 'Create'}
              </button>
            </div>

            {!valid && !busy && missingReason && (
              <p className="hint hint--error">{missingReason}</p>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
